import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { MapPin, ExternalLink, Building2, Plane, Smartphone } from "lucide-react";
import { PageHeader, Spinner } from "../components/common";
import { ReportUpdate } from "./RailStation";
import { MODE_CONFIG, TransportRouteCard } from "./TransportVertical";
import DiscussionBoard from "../components/DiscussionBoard";
import AskCommunityCTA from "../components/AskCommunityCTA";
import api from "../lib/api";

export function TransportHubPage() {
  const { mode, id } = useParams();
  const cfg = MODE_CONFIG[mode] || {};
  const [hub, setHub] = useState(null);
  const [error, setError] = useState(false);
  useEffect(() => {
    setHub(null); setError(false);
    api.get(`/transport/${mode}/hubs/${id}`).then((r) => setHub(r.data)).catch(() => setError(true));
  }, [mode, id]);

  if (error) return <NotFound mode={mode} />;
  if (!hub) return <Spinner full />;
  return (
    <div data-testid={`transport-hub-page-${hub.id}`}>
      <PageHeader eyebrow={`${cfg.title || mode} · ${hub.country}`} title={hub.name}
        subtitle={`${hub.city}, ${hub.country} · ${(hub.hub_type || "").replaceAll("_", " ")}`} entityCode={cfg.code || mode} />
      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-10 grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-8">
          <section className="border border-zinc-300 bg-white p-6">
            <h2 className="font-heading font-900 text-xl mb-4">Quick information</h2>
            <dl className="grid sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
              <Info label="Location" value={`${hub.city}, ${hub.country}`} />
              <Info label="Type" value={(hub.hub_type || "—").replaceAll("_", " ")} />
              <Info label="Coordinates" value={`${hub.lat?.toFixed(4)}, ${hub.lon?.toFixed(4)}`} />
              <Info label="Accessibility" value={hub.accessibility_status === "unknown" ? "Requires verification" : hub.accessibility_status} />
            </dl>
            <div className="mt-4 flex flex-wrap gap-3 text-xs">
              <a href={`https://www.openstreetmap.org/?mlat=${hub.lat}&mlon=${hub.lon}#map=16/${hub.lat}/${hub.lon}`} target="_blank" rel="noreferrer"
                className="inline-flex items-center gap-1 font-700 text-primary hover:underline"><MapPin className="w-3.5 h-3.5" /> View on map ↗</a>
              <span className="text-muted-foreground">Last verified {hub.last_verified} · Confidence {hub.confidence}</span>
            </div>
          </section>
          {hub.routes?.length > 0 && (
            <section>
              <h2 className="font-heading font-900 text-xl mb-4">Services from here</h2>
              <div className="grid sm:grid-cols-2 gap-4">{hub.routes.map((r) => <TransportRouteCard key={r.id} r={r} />)}</div>
            </section>
          )}
          <section id="discussion" className="scroll-mt-24 space-y-5" data-testid="transport-hub-discussion">
            <AskCommunityCTA entityName={hub.name} targetId="discussion" />
            <DiscussionBoard entityType="transport_hub" entityId={`${mode}-${hub.id}`} entityName={hub.name}
              placeholder={`Delays, tips, connections, accessibility — share or ask about ${hub.name}…`} />
          </section>
        </div>
        <aside className="space-y-6">
          {hub.operator_details?.length > 0 && (
            <section className="border border-zinc-300 bg-white p-5">
              <h3 className="font-heading font-700 text-lg mb-3 flex items-center gap-2"><Building2 className="w-4 h-4" /> Operators</h3>
              {hub.operator_details.map((o) => (
                <Link key={o.id} to={`/t/${o.mode}/operators/${o.id}`} className="block py-2 border-b border-zinc-100 last:border-0 text-sm hover:text-primary">
                  <span className="font-600">{o.common_name}</span><span className="text-muted-foreground"> · {o.country}</span>
                </Link>
              ))}
            </section>
          )}
          <section className="border border-zinc-300 bg-white p-5" data-testid="hub-connections">
            <h3 className="font-heading font-700 text-lg mb-3">Transportation connections</h3>
            <div className="flex flex-wrap gap-2">
              {(hub.connections || []).map((c) => (
                <span key={c} className="border border-zinc-300 px-2.5 py-1 text-xs capitalize">{c.replaceAll("_", " ")}</span>
              ))}
            </div>
            {hub.nearby_airport && (
              <Link to={`/airports?q=${hub.nearby_airport}`}
                className="mt-3 flex items-center gap-2 border border-sky-200 bg-sky-50 px-3 py-2 text-sm text-sky-900 hover:border-sky-400 transition-colors">
                <Plane className="w-4 h-4" /> Nearest major airport: <span className="font-mono font-700">{hub.nearby_airport}</span>
              </Link>
            )}
          </section>
          <ReportUpdate entityType="hub" entityId={hub.id} mode={mode} />
          <Link to={`/${mode === "stay" ? "stays" : mode}`} className="block text-center border border-zinc-300 bg-white px-4 py-2.5 text-sm font-700 hover:border-primary transition-colors">← Back to {cfg.title}</Link>
        </aside>
      </div>
    </div>
  );
}

export function TransportOperatorPage() {
  const { mode, id } = useParams();
  const cfg = MODE_CONFIG[mode] || {};
  const [op, setOp] = useState(null);
  const [error, setError] = useState(false);
  useEffect(() => {
    setOp(null); setError(false);
    api.get(`/transport/${mode}/operators/${id}`).then((r) => setOp(r.data)).catch(() => setError(true));
  }, [mode, id]);

  if (error) return <NotFound mode={mode} />;
  if (!op) return <Spinner full />;
  return (
    <div data-testid={`transport-operator-page-${op.id}`}>
      <PageHeader eyebrow={`${cfg.title || mode} · ${op.country}`} title={op.common_name} subtitle={op.description} entityCode={op.country_code} />
      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-10 grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-8">
          {op.routes?.length > 0 && (
            <section>
              <h2 className="font-heading font-900 text-xl mb-4">Notable services</h2>
              <div className="grid sm:grid-cols-2 gap-4">{op.routes.map((r) => <TransportRouteCard key={r.id} r={r} />)}</div>
            </section>
          )}
          {op.hubs?.length > 0 && (
            <section className="border border-zinc-300 bg-white p-6">
              <h2 className="font-heading font-900 text-xl mb-4">Hubs served</h2>
              <div className="grid sm:grid-cols-2 gap-2">
                {op.hubs.map((h) => (
                  <Link key={h.id} to={`/t/${h.mode}/hubs/${h.id}`} className="flex items-center gap-2 py-1.5 text-sm hover:text-primary">
                    <MapPin className="w-3.5 h-3.5 shrink-0 text-primary" />{h.name} <span className="text-muted-foreground">· {h.city}</span>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </div>
        <aside className="space-y-6">
          <section className="border border-zinc-300 bg-white p-5 text-sm space-y-3">
            <h3 className="font-heading font-700 text-lg">Provider profile</h3>
            <Info label="Official name" value={op.name} />
            <Info label="Country" value={op.country} />
            <Info label="Type" value={(op.type || "—").replaceAll("_", " ")} />
            <Info label="Service area" value={op.service_area} />
            {op.service_types?.length > 0 && <Info label="Services" value={op.service_types.join(", ").replaceAll("_", " ")} />}
            {op.mobile_app && <Info label="Mobile app" value={op.mobile_app} icon={<Smartphone className="w-3.5 h-3.5 text-primary" />} />}
            <a href={op.website} target="_blank" rel="noreferrer" data-testid="transport-operator-website"
              className="inline-flex items-center gap-1.5 font-700 text-primary hover:underline">Official website <ExternalLink className="w-3.5 h-3.5" /></a>
            <p className="text-xs text-muted-foreground pt-1">Last verified {op.last_verified} · Confidence {op.confidence}</p>
          </section>
          <ReportUpdate entityType="operator" entityId={op.id} mode={mode} />
          <Link to={`/${mode === "stay" ? "stays" : mode}`} className="block text-center border border-zinc-300 bg-white px-4 py-2.5 text-sm font-700 hover:border-primary transition-colors">← Back to {cfg.title}</Link>
        </aside>
      </div>
    </div>
  );
}

const Info = ({ label, value, icon }) => (
  <div><div className="text-xs font-mono uppercase tracking-wide text-muted-foreground">{label}</div>
    <div className="font-600 capitalize flex items-center gap-1.5">{icon}{value}</div></div>
);

const NotFound = ({ mode }) => (
  <div className="max-w-4xl mx-auto p-10 text-center text-muted-foreground">
    Not found. <Link to={`/${mode === "stay" ? "stays" : mode}`} className="text-primary underline">Go back</Link>
  </div>
);
